import { useLayoutEffect } from "react";
import { useWatch } from "react-hook-form";
import { GenericAsyncSelect } from "components/Form/Select/GenericAsyncSelect";
import { ControlledSelectProps } from "components/Form/Select";
import { API_ROUTES } from "definitions/constants/routeConstants";
import { User } from "features/User/models";
import { useFloatingField } from "./context";
import { sizeStyles } from "./sizeStyles";

type FloatingUserSelectProps = Pick<
  ControlledSelectProps,
  | "control"
  | "name"
  | "isClearable"
  | "cacheKey"
  | "chakraStyles"
  | "onChangeCallback"
  | "rules"
  | "readOnly"
  | "isDisabled"
> & {
  excludedIds?: string[];
  perPagePagination?: number;
  onBlur?: () => void;
};

function FloatingUserSelect({
  control,
  name,
  onChangeCallback,
  chakraStyles,
  placeholder = "",
  isClearable = true,
  perPagePagination = 300,
  onBlur: onBlurProp,
  ...rest
}: FloatingUserSelectProps & { placeholder?: string }) {
  const { setHasValue, size } = useFloatingField();
  const styles = sizeStyles[size];
  const value = useWatch({ control, name });

  useLayoutEffect(() => {
    setHasValue(!!value);
  }, [value, setHasValue]);

  return (
    <div className="floating-single-select">
      <GenericAsyncSelect<User>
        fetchPath={API_ROUTES.users.search}
        perPagePagination={perPagePagination}
        embedOption={(user) => ({
          value: user.id,
          label: user.fullName ?? "",
          rawValue: user,
        })}
        extractValue={(user) => user.id}
        control={control}
        name={name}
        placeholder={placeholder}
        isClearable={isClearable}
        onChangeCallback={(val) => {
          setHasValue(!!val);
          onChangeCallback?.(val);
          onBlurProp?.();
        }}
        chakraStyles={{
          control: (provided: any) => ({
            ...provided,
            height: styles.inputHeight,
            minHeight: styles.inputHeight,
            maxHeight: styles.inputHeight,
            backgroundColor: "transparent",
          }),
          valueContainer: (provided: any) => ({
            ...provided,
            paddingLeft: 0,
            paddingInlineStart: 0,
          }),
          ...chakraStyles,
        }}
        {...rest}
      />
    </div>
  );
}

export default FloatingUserSelect;
